// ─────────────────────────────────────────────────────────────
// BOOTCAMP CLASS READY™ — all copy and links live here. Image URLs live in data/images.ts, the pixel in data/pixel.ts.
// Same structure as Martial Arts Kids Class Ready (modeled on Managers), with a NEW palette (night navy sections,
// energy-orange buttons, electric-lime highlights, condensed athletic titles), NEW button copy and a NEW section
// (3 options at every station + the timers used all year), to test what converts.
// Promise (must match ads, checkout and the PDF): 52-week class plan + 10 complete 45-minute bootcamp classes,
// $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $27, $37 ($155); total $314 → $27, you save $287.
// Fitness: for qualified professionals; no weight-loss, fat-loss, calorie or medical claims; no testimonials.
// ─────────────────────────────────────────────────────────────

export { META_PIXEL_ID } from './pixel';
export const CHECKOUT_URL = 'https://bootcamp-class-ready.impultienda.ar/checkout';

export const PAGE_TITLE = 'Bootcamp Class Ready™ | Your Whole Year of Bootcamp Classes, Already Planned';
export const META_DESCRIPTION =
  'A 52-week class plan plus 10 complete, ready-to-coach 45-minute bootcamp classes for mixed-level groups. Open this week’s page, set up in 5 minutes and coach it today.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'BOOTCAMP CLASS READY™';
/** Label for every button that scrolls to the offer section (new copy for this landing) */
export const ctaLabel = 'GET MY YEAR OF CLASSES';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY YEAR OF CLASSES · $27';
export const topBarMobileLabel = '52 WEEKS · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR PERSONAL TRAINERS, BOOTCAMP COACHES & GROUP FITNESS INSTRUCTORS',
  titleLead: 'YOUR WHOLE YEAR OF BOOTCAMP CLASSES,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 52-week class plan plus 10 complete, ready-to-coach 45-minute classes for mixed-level groups. Open this week’s page and coach it today.',
  mockupAlt: 'Bootcamp Class Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE CLASSES' },
  { value: '45', label: 'MINUTES, FULLY TIMED' },
  { value: '3', label: 'OPTIONS FOR EVERY EXERCISE' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'THE NIGHT BEFORE VS. IN CLASS.',
  before: {
    label: 'BEFORE',
    when: 'The night before',
    quote: '“What do we do in class tomorrow?”',
    items: ['Scrolling for workout ideas.', 'The same circuit again.', 'Beginners lost, regulars bored.'],
  },
  after: {
    label: 'AFTER',
    when: 'In class',
    items: ['Open this week’s page.', 'Set up the stations in 5 minutes.', 'Coach the 45-minute class block.', 'Finish strong and send the client message.'],
  },
};

// ── 3. WHAT DOES YOUR GROUP NEED THIS WEEK (pick a need, see the class that is ready for it) ──
const block = 'Class block: warm-up 8 · movement prep & demo 5 · main workout 20 · finisher 7 · cool-down 5';
export const selector = {
  eyebrow: 'START WHERE YOUR CLIENTS ARE',
  title: 'WHAT DOES YOUR GROUP NEED THIS WEEK?',
  sub: 'Pick a need and see the class that is ready for it.',
  startLabel: 'YOUR NEXT CLASS',
  tablistLabel: 'Class needs',
  columns: ['PAGE 1 · CLASS PLAN', 'PAGE 2 · COACH IT', 'PAGE 3 · RUN IT'],
  note: 'All 10 classes follow this same 3-page structure. Adapt each one to your clients, your space and your gym’s safety rules.',
  items: [
    {
      tab: 'A strong first class',
      n: 'CLASS 01 · WEEK 1',
      title: 'Foundations: Squat, Hinge, Push, Pull & Brace',
      goal: 'Every client learns the five basic moves with good form and finds their option at every station.',
      open: ['Class goal, equipment and 5-minute set-up', 'Your opening words: “The right option is the one you can do with good form.”', block],
      activity: ['8-minute warm-up and a demo of the squat and the hinge', '6 station cards, each with an easier and a harder option and one cue', '3 Fix It cues, like “Push your knees out over your toes!”'],
      close: ['Station layout diagram and a 40/20 timer', 'Finisher: the 10-10-10', 'Cool-down, closing talk and a ready-to-send client message'],
    },
    {
      tab: 'More cardio and agility',
      n: 'CLASS 05 · WEEK 18',
      title: 'Cardio Cones & Agility',
      goal: 'Clients shuffle, brake, change direction and land softly, each at their own speed.',
      open: ['Class goal, equipment and 5-minute set-up', 'Your opening words: “Fast feet, soft landings, small steps to brake.”', block],
      activity: ['Warm-up and a demo of the shuffle and the brake & turn', '6 cone and ladder stations with easier and harder options', '3 Fix It cues for the most common footwork errors'],
      close: ['6-lane layout diagram and a 30/30 timer', 'Finisher: Four-Corner Cardio', 'Cool-down, closing talk and client message'],
    },
    {
      tab: 'A partner class',
      n: 'CLASS 07 · WEEK 27',
      title: 'Partner Bootcamp: You Go, I Go',
      goal: 'Partners take turns, cheer each other on and keep moving, each at their own option.',
      open: ['Class goal, equipment and 5-minute set-up', 'Your opening words: “One works, one holds. Every 30 seconds you swap.”', block],
      activity: ['Partner warm-up and a demo of the high-five plank', '6 partner stations: one works, one holds, with easier and harder options', '3 Fix It cues to keep every pair working'],
      close: ['Circle layout with 2 spots per station and a 3-minute timer', 'Finisher: High-Five Plank', 'Cool-down, closing talk and client message'],
    },
    {
      tab: 'A progress check day',
      n: 'CLASS 10 · WEEK 40',
      title: 'Benchmark Bootcamp: The Progress Check Class',
      goal: 'Clients complete 4 simple tests with a partner, write down their numbers and finish with a team finale.',
      open: ['Class goal, equipment and 5-minute set-up', 'Your opening words: “It is not a competition: it is your number, today.”', 'Class block: warm-up 8 · movement prep 4 · progress tests 15 · team finale 12 · cool-down & feedback 6'],
      activity: ['4 test cards with the rep standard for each test', 'How to record: the number and the option', 'A first-timer card and safety notes'],
      close: ['Team finale: “The Year in 12 Minutes”', 'Progress check sheet and feedback script, word for word', 'A client message with their numbers'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages in data/images.ts) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE NEXT TO THE SPEAKER.',
  sub: 'Open any page and read it at full size: your 52-week plan, a complete class in 3 pages, the progress tests and your class planner.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full book.',
  captions: [
    'Your 52-week plan (Q1)',
    'Class 02 · class plan',
    'Class 02 · coach it',
    'Class 02 · run it',
    'Class 10 · progress tests',
    'Class planner + progress tracker',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY CLASS',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST SHOW UP AND COACH.',
  text: 'Every week of the year is mapped: a focus, a format and a finisher, the start of one of the 10 complete classes or a progress check. Each class gives you the plan, the stations and the run sheet in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 52-week plan.' },
    { title: 'SET UP', text: 'Read the page and set up the stations in 5 minutes.' },
    { title: 'COACH', text: 'Warm-up, demo and the 20-minute main workout.' },
    { title: 'FINISH', text: 'Finisher, cool-down and the client message.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE CLASS',
  features: [
    { icon: 'goal', title: 'One clear class goal', text: 'One sentence of what your clients will do, the week to start it and what they need first.' },
    { icon: 'timer', title: 'A 45-minute class block', text: 'Warm-up 8 · Movement prep & demo 5 · Main workout 20 · Finisher 7 · Cool-down 5.' },
    { icon: 'steps', title: '6 station cards', text: 'Every exercise with an easier and a harder option and the one cue to say.' },
    { icon: 'questions', title: 'Station layout + timer', text: 'A top-view diagram of where everything goes, how clients rotate and the work/rest timer.' },
    { icon: 'quote', title: 'Your words, word for word', text: 'Opening words, cues, Fix It corrections and the closing talk.' },
    { icon: 'action', title: 'Finisher + client message', text: 'A finisher, 5 stretches with times, a progress note and a ready-to-send client message.' },
  ],
} as const;

// ── 5b. 3 OPTIONS + TIMERS (new section in this landing) ──────────────────
export const optionsBand = {
  eyebrow: 'EVERY CLIENT GETS AN OPTION',
  title: 'ONE CLASS. THREE OPTIONS AT EVERY STATION.',
  sub: 'Beginners and your fittest regulars do the same class. Every exercise has an easier and a harder option, so nobody is lost and nobody is bored.',
  levels: ['EASIER', 'STANDARD', 'HARDER'],
  examples: [
    { station: 'PUSH-UP', options: ['Hands on a wall or bench', 'Push-up', 'Full push-up, 2-second lower'] },
    { station: 'GOBLET SQUAT', options: ['Squat to a bench', 'Goblet squat', 'Heavier, 3-second lower'] },
    { station: 'SKATERS', options: ['Step behind', 'Skaters', 'Jump side to side'] },
  ],
  timersTitle: 'THE FORMATS YOU’LL RUN ALL YEAR',
  timers: [
    { name: '40/20', text: '40 s work · 20 s rest' },
    { name: '30/30', text: '30 s work · 30 s rest' },
    { name: '60/30', text: 'strength: 60 s work · 30 s rest' },
    { name: '20/10', text: 'short, sharp intervals' },
    { name: 'EMOM', text: 'every minute on the minute' },
    { name: 'AMRAP', text: 'as many rounds as possible' },
    { name: 'PARTNERS', text: 'you go, I go' },
    { name: 'TEAMS', text: 'relays and group goals' },
  ],
};

// ── 6. THE 52-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 52-WEEK CLASS PLAN',
  title: 'THE WHOLE YEAR, MAPPED WEEK BY WEEK.',
  sub: '★ weeks start a new class. ◆ weeks are progress checks. Every week in between has a focus, a format and a finisher.',
  quarters: [
    {
      q: 'Q1',
      weeks: 'WEEKS 1–13',
      name: 'BUILD THE FOUNDATIONS',
      full: [
        { week: '1', label: 'START CLASS 01', title: 'Foundations: Squat, Hinge, Push, Pull & Brace' },
        { week: '5', label: 'START CLASS 02', title: 'Lower Body Strength' },
        { week: '9', label: 'START CLASS 03', title: 'Upper Body Push & Pull' },
        { week: '13', label: 'PROGRESS CHECK', title: '4 tests + the progress tracker', review: true },
      ],
      activities: '+ a focus, a format and a finisher for every week in between, like Wall Sit Challenge, Band Pull-Apart 100 and Step-Up Countdown.',
    },
    {
      q: 'Q2',
      weeks: 'WEEKS 14–26',
      name: 'STRENGTH & STABILITY',
      full: [
        { week: '14', label: 'START CLASS 04', title: 'Core & Stability' },
        { week: '18', label: 'START CLASS 05', title: 'Cardio Cones & Agility' },
        { week: '22', label: 'START CLASS 06', title: 'Total Body Dumbbell Strength' },
        { week: '26', label: 'PROGRESS CHECK', title: '4 tests + the progress tracker', review: true },
      ],
      activities: '+ a focus, a format and a finisher for every week in between, like Side Plank Switch, Red Light, Green Light and Squat-to-Press Ladder.',
    },
    {
      q: 'Q3',
      weeks: 'WEEKS 27–39',
      name: 'CONDITIONING & TEAMWORK',
      full: [
        { week: '27', label: 'START CLASS 07', title: 'Partner Bootcamp: You Go, I Go' },
        { week: '31', label: 'START CLASS 08', title: 'Team Relays & Group AMRAP' },
        { week: '35', label: 'START CLASS 09', title: '20/10 Interval Blocks' },
        { week: '39', label: 'PROGRESS CHECK', title: '4 tests + the progress tracker', review: true },
      ],
      activities: '+ a focus, a format and a finisher for every week in between, like Mirror Squats, Medicine Ball Pass Relay and Skater Countdown.',
    },
    {
      q: 'Q4',
      weeks: 'WEEKS 40–52',
      name: 'PUT IT ALL TOGETHER',
      full: [
        { week: '40', label: 'START CLASS 10', title: 'Benchmark Bootcamp: The Progress Check Class' },
        { week: '52', label: 'PROGRESS CHECK', title: 'Year-end progress check', review: true },
      ],
      activities: '+ review weeks, an outdoor park class, a bring-a-friend class and a clients’ choice week.',
    },
  ],
  toolsTitle: 'PLUS YOUR COACH TOOLS',
  tools: ['When the class gets hard: 8 situations, what to say and do', 'Class planner + progress tracker: plan any class in 5 minutes', 'Class field card to keep next to the speaker'],
};

// ── 7. THE 10 COMPLETE CLASSES ─────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE CLASSES',
  title: 'TEN BOOTCAMP CLASSES. READY TO COACH.',
  sub: 'Follow the 52-week plan, or start with the class your group needs now. Each one is 3 pages: class plan, coach it, run it.',
  weekLabel: 'START WEEK',
  items: [
    { n: '01', week: '1', title: 'Foundations: Squat, Hinge, Push, Pull & Brace', goal: 'The five basic moves with good form, and every client finds their option at every station.' },
    { n: '02', week: '5', title: 'Lower Body Strength', goal: 'Squats, lunges, hinges and step-ups with control and the right dumbbell weight.' },
    { n: '03', week: '9', title: 'Upper Body Push & Pull', goal: 'Push, pull and press with good posture, balancing every push with a pull.' },
    { n: '04', week: '14', title: 'Core & Stability', goal: 'Brace, resist rotation and hold stable positions while breathing well.' },
    { n: '05', week: '18', title: 'Cardio Cones & Agility', goal: 'Shuffle, brake, change direction and land softly, each at their own speed.' },
    { n: '06', week: '22', title: 'Total Body Dumbbell Strength', goal: '60 seconds of good form at every station with a challenging, controlled weight.' },
    { n: '07', week: '27', title: 'Partner Bootcamp: You Go, I Go', goal: 'Partners take turns, cheer each other on and keep moving the whole workout.' },
    { n: '08', week: '31', title: 'Team Relays & Group AMRAP', goal: 'Teams of 3 work through a 15-minute team AMRAP and a relay, each at their own option.' },
    { n: '09', week: '35', title: '20/10 Interval Blocks', goal: '20 seconds hard, 10 seconds rest, with good form through 3 short interval blocks.' },
    { n: '10', week: '40', title: 'Benchmark Bootcamp: The Progress Check Class', goal: '4 simple progress tests with a partner and a team finale with moves from the whole year.' },
  ],
  cta: 'GET ALL 10 CLASSES + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 BOOK',
  title: 'EVERYTHING ELSE YOUR CLASSES NEED.',
  sub: 'Station exercises, small-space classes, finishers, coaching cues and a client welcome pack, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '100 Station Exercises',
      hook: 'Never run out of stations again.',
      title: '100 Station Exercises — Easy & Hard Options for Every Move',
      text: '100 station exercises organized by goal: legs, push, pull, core, cardio and agility. Each with simple steps, the key cue, the most common mistake, an easier and a harder option and the equipment it needs.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: '10 Small-Space & Outdoor Classes',
      hook: 'No full gym? Still a full class.',
      title: '10 Small-Space & Outdoor Classes — For Parks, Garages and Small Studios',
      text: '10 complete 45-minute classes: 5 for parks and outdoor spaces and 5 for small studios, garages or living-room sessions with minimal equipment. Timed block, station layout, work and rest times and coaching cues.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: '52 Partner & Team Finishers',
      hook: 'End every class with energy.',
      title: '52 Partner & Team Finishers — Fun Endings Your Clients Will Love',
      text: '52 short finishers of 5 to 8 minutes, one for every week of the year: partner challenges, team relays, ladders, countdowns and group goals. Each with the setup, rules, timing, how to scale it and a safety note.',
    },
    {
      label: 'BONUS 04',
      value: '$27',
      short: 'Coach’s Cue Cards',
      hook: 'Always know what to say.',
      title: 'Coach’s Cue Cards — What to Say in Every Part of the Class',
      text: 'How to open the class and explain the format in 60 seconds, short form cues for the most common exercises, lines for the hardest minutes, how to correct without stopping the class, transition calls and how to close.',
    },
    {
      label: 'BONUS 05',
      value: '$37',
      short: 'Client Welcome & Retention Pack',
      hook: 'Welcome new clients the right way.',
      title: 'Client Welcome & Retention Pack — Intake Form, Messages & a 30-Day Challenge',
      text: 'A simple health and goals intake form, a first-class welcome script, ready-to-send messages for the first week, missed-class and comeback messages, a 6-week progress check and a 30-day attendance challenge with a printable tracker.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT CLASS STARTS HERE',
  title: 'YOUR WHOLE YEAR OF BOOTCAMP CLASSES. ALL IN ONE BOOK.',
  kicker: 'THE COMPLETE DIGITAL BOOK',
  name: 'Bootcamp Class Ready™',
  description: 'A 52-week class plan and 10 complete, ready-to-coach 45-minute bootcamp classes, plus five bonuses: station exercises, small-space and outdoor classes, finishers, coaching cue cards and a client welcome pack.',
  mockupAlt: 'Everything included in Bootcamp Class Ready™',
  includedTitle: 'YOUR BOOK INCLUDES',
  included: [
    '52-week class plan: the whole year mapped',
    '10 complete bootcamp classes, 3 pages each',
    'A timed 45-minute class block in every class',
    '6 stations with easier and harder options',
    'Station layout diagrams and work/rest timers',
    'Cues, finisher, cool-down and client message',
    'Coach tools: hard moments, planner, field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Bootcamp Class Ready™ (52-week plan + 10 complete classes)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout (new copy for this landing) */
  cta: 'YES, SEND ME MY CLASSES — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. For qualified fitness professionals: adapt every class to your clients, your space and your gym’s safety rules.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Coach your next classes with it. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOUR NEXT CLASS.',
  sub: 'Built for coaches who run group classes every week. A ready-to-coach class book, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF book: a 52-week class plan (every week has a focus, a format and a finisher, the start of a new class or a progress check) and 10 complete 45-minute bootcamp classes of 3 pages each, plus coach tools and 5 bonuses.',
    },
    {
      q: 'Who is it for?',
      a: 'Personal trainers, group fitness instructors, bootcamp coaches and studio or small gym owners who coach group classes for adults of mixed ages and fitness levels.',
    },
    {
      q: 'What equipment do I need?',
      a: 'Simple equipment most bootcamps already have: mats, dumbbells or kettlebells, resistance bands and mini bands, cones, a sturdy step or bench, a medicine ball and a timer. Short on equipment? The book shows you how to swap in bodyweight versions.',
    },
    {
      q: 'Does it work for mixed levels?',
      a: 'Yes. Every exercise has an easier and a harder option, and every client chooses the option they can do with good form. Beginners and regulars do the same class.',
    },
    {
      q: 'Can I use it outdoors?',
      a: 'Yes. The classes work in a studio, a gym or a park, and the plan includes an outdoor park class. Check the ground, the shade and the weather before every outdoor class.',
    },
    {
      q: 'Is this a weight-loss or medical program?',
      a: 'No. It is class material for qualified fitness professionals and makes no weight-loss, fat-loss or medical claims. New clients complete your usual health screening, and you adapt every class to your clients and your gym’s safety rules.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Can I use the classes individually?',
      a: 'Yes. Each class stands alone, so you can start with the one your group needs now, or follow the 52-week plan from week 1.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet during class, and the class planner and progress tracker are ready to print.',
    },
    {
      q: 'Can I share it with my trainers?',
      a: 'This purchase is for your own use as a coach. Sharing it across several gyms or studios, redistributing it or reselling it is not included in this offer.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO WE DO IN CLASS TODAY?”',
  title: 'WALK INTO CLASS WITH TODAY’S WORKOUT READY.',
  text: 'Open this week’s page. Set up in 5 minutes. Coach a class your clients want to come back to.',
  microcopy: '52-week plan + 10 complete classes + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'BOOTCAMP CLASS READY™ · Class material for qualified fitness professionals. Adapt every class to your clients, your space and your gym’s safety rules.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
